import { Component, ViewEncapsulation, Inject, Optional, ChangeDetectionStrategy } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RingService, RingStrength } from 'src/app/modules/general/services/ring.service';
import confetti from 'canvas-confetti';

type DialogData = {
  times?: number;
  strength?: RingStrength;
  autoRun?: boolean;
  closeOnFinish?: boolean; // kept for compatibility, but we close immediately now
};

@Component({
  selector: 'app-ring',
  imports: [MatButtonModule, MatIconModule, MatDialogModule],
  templateUrl: './ring.component.html',
  styleUrls: ['./ring.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None
})
export class RingComponent {
  // Choices
  public timesOptions: number[] = [1, 2, 3, 4, 5];
  public strengthOptions: { value: RingStrength; label: string; icon: string }[] = [
    { value: 'soft', label: 'Zacht', icon: 'volume_mute' },
    { value: 'normal', label: 'Normaal', icon: 'volume_down' },
    { value: 'hard', label: 'Hard', icon: 'volume_up' },
    { value: 'mega', label: 'Mega', icon: 'celebration' }
  ];

  // Selected state
  public selectedTimes: number = 1;
  public selectedStrength: RingStrength = 'normal';

  // UI state
  public isRinging: boolean = false;

  public constructor(
    private readonly ringService: RingService,
    @Optional() private readonly dialogRef?: MatDialogRef<RingComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private readonly data?: DialogData
  ) {
    // Initialize from dialog data if provided
    if (this.data?.times !== undefined) {
      this.selectedTimes = this.data.times;
    }
    if (this.data?.strength !== undefined) {
      this.selectedStrength = this.data.strength;
    }

    // Optional autorun (will also close immediately)
    if (this.data?.autoRun === true) {
      queueMicrotask(() => this.ringNow());
    }
  }

  public setTimes(value: number): void {
    this.selectedTimes = value;
  }

  public setStrength(value: RingStrength): void {
    this.selectedStrength = value;
  }

  // Fire the real bell via ESP32, close modal immediately, show fireworks after API completes
  public ringNow(): void {
    this.isRinging = true;

    this.ringService
      .ring(this.selectedTimes, this.selectedStrength, { light: true })
      .subscribe({
        next: () => {
          this.triggerConfetti();
        },
        error: (err: unknown) => {
          // Optional: you could surface a snackbar here
          console.error('Bell request failed:', err);
        },
        complete: () => {
          this.isRinging = false;
        }
      });

    // Close the dialog immediately upon firing
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  // Nice confetti burst after API success
  private triggerConfetti(): void {
    // Center burst
    confetti({ particleCount: 120, spread: 70, startVelocity: 45, origin: { y: 0.25 } });
    // Side salvos
    confetti({ particleCount: 80, angle: 60, spread: 60, origin: { x: 0, y: 0.6 } });
    confetti({ particleCount: 80, angle: 120, spread: 60, origin: { x: 1, y: 0.6 } });
    // Small follow-up sparkle
    window.setTimeout(() => {
      confetti({ particleCount: 60, spread: 80, startVelocity: 35, origin: { y: 0.3 } });
    }, 300);
  }
}
